import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Search, Send, Shield } from "lucide-react";
import { toast } from "sonner";

export default function MessagesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  if (!user) return null;
  return <ConversationPage key={JSON.stringify([user?.id, userId])} userId={userId} user={user} navigate={navigate} />;
}

function ConversationPage({ userId, user, navigate }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const endRef = useRef(null);
  const mounted = useRef(false);
  const sendingRef = useRef(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const loadConvs = useCallback(async () => {
    try {
      const r = await api.get("/messages/conversations", { timeout: 30000 });
      if (mounted.current) setConvs(r.data);
    } catch (err) {
      console.warn("Failed to load conversations", err);
    }
  }, []);
  useEffect(() => { loadConvs(); }, [loadConvs]);

  useEffect(() => {
    let current = true;
    if (!userId) { setActive(null); setMessages([]); return; }
    (async () => {
      try {
        const u = await api.get(`/users/${userId}`, { timeout: 30000 });
        if (!current) return;
        const m = await api.get(`/messages/${userId}`, { timeout: 30000 });
        if (!current) return;
        setActive(u.data);
        setMessages(m.data);
      } catch (err) {
        console.warn("Failed to load conversation", err);
      }
    })();
    return () => { current = false; };
  }, [userId]);

  useEffect(() => {
    let current = true;
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/search?q=${encodeURIComponent(q)}`, { timeout: 30000 });
        if (current) setResults(r.data);
      } catch (err) {
        console.warn("User search failed", err);
      }
    }, 200);
    return () => { current = false; clearTimeout(t); };
  }, [q]);

  const send = async (e) => {
    e.preventDefault();
    if (sendingRef.current || !text.trim() || !active || active.id !== userId) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const r = await api.post("/messages", { recipient_id: active.id, content: text }, { timeout: 30000 });
      if (!mounted.current) return;
      setMessages((current) => [...current, r.data]);
      if (r.data.moderation?.flag) toast.warning("Gentle nudge: " + (r.data.moderation.reason || "kinder phrasing recommended"));
      setText("");
      loadConvs();
    } catch (err) {
      if (mounted.current) {
        console.warn("Send message failed", err);
        toast.error("Could not send");
      }
    } finally {
      sendingRef.current = false;
      if (mounted.current) setSending(false);
    }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-180px)]" data-testid="messages-page">
      <aside className="brutal-card bg-card p-3 overflow-y-auto">
        <div className="relative mb-3">
          <Search size={14} strokeWidth={2.5} className="absolute left-3 top-3" />
          <input data-testid="search-users-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find someone…" className="brutal-input w-full pl-8 pr-3 py-2 text-sm" />
        </div>
        {results.length > 0 && (
          <div className="space-y-1 mb-3 border-b-2 border-foreground pb-3">
            {results.map((u) => (
              <button key={u.id} onClick={() => { setQ(""); setResults([]); navigate(`/messages/${u.id}`); }}
                data-testid={`search-result-${u.id}`}
                className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-secondary border-2 border-transparent hover:border-foreground">
                <div className="w-8 h-8 rounded-full border-2 border-foreground bg-accent flex items-center justify-center text-xs font-bold overflow-hidden">
                  {u.avatar_path ? <img src={fileUrl(u.avatar_path)} className="w-full h-full object-cover" alt="" /> : u.display_name?.[0]}
                </div>
                <div className="text-sm"><div className="font-semibold">{u.display_name}</div><div className="text-xs text-muted-foreground">@{u.username}</div></div>
              </button>
            ))}
          </div>
        )}

        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Conversations</div>
        {convs.length === 0 && <p className="text-sm text-muted-foreground p-2">No conversations yet. Search to start one.</p>}
        {convs.map((c) => (
          <button key={c.conversation_id} onClick={() => navigate(`/messages/${c.other_user.id}`)}
            data-testid={`conv-${c.other_user.id}`}
            className={`w-full text-left flex items-center gap-2 p-2 mb-1 rounded-lg border-2 ${active?.id === c.other_user.id ? "border-foreground bg-secondary" : "border-transparent hover:border-foreground"}`}>
            <div className="w-9 h-9 rounded-full border-2 border-foreground bg-accent flex items-center justify-center text-xs font-bold overflow-hidden">
              {c.other_user.avatar_path ? <img src={fileUrl(c.other_user.avatar_path)} className="w-full h-full object-cover" alt="" /> : c.other_user.display_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{c.other_user.display_name}</div>
              <div className="text-xs text-muted-foreground truncate">{c.last_message.from_me ? "You: " : ""}{c.last_message.content}</div>
            </div>
          </button>
        ))}
      </aside>

      <section className="brutal-card bg-card flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground p-4 text-center">Select a conversation or search to start a new one.</div>
        ) : (
          <>
            <header className="border-b-2 border-foreground p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full border-2 border-foreground bg-accent flex items-center justify-center text-xs font-bold overflow-hidden">
                {active.avatar_path ? <img src={fileUrl(active.avatar_path)} className="w-full h-full object-cover" alt="" /> : active.display_name?.[0]}
              </div>
              <div><div className="font-semibold">{active.display_name}</div><div className="text-xs text-muted-foreground">@{active.username}</div></div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.length === 0 && <div className="text-sm text-muted-foreground">Say something kind to break the ice.</div>}
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[75%] brutal-card p-3 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
                      {m.moderation?.flag && (
                        <div className="text-xs flex items-center gap-1 mb-1 opacity-80"><Shield size={12} strokeWidth={2.5} /> care AI flagged</div>
                      )}
                      {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                      {m.image_path && (
                        <img
                          src={fileUrl(m.image_path)}
                          alt="Message attachment"
                          className="mt-2 max-h-72 rounded-lg border-2 border-foreground object-contain"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t-2 border-foreground p-3 flex gap-2">
              <input data-testid="message-input" disabled={sending} value={text} onChange={(e) => setText(e.target.value)} maxLength={5000} placeholder="Type a kind message…" className="brutal-input flex-1 px-3 py-2 text-sm" />
              <button data-testid="send-message-btn" disabled={sending || !text.trim()} aria-label="Send message" type="submit" className="brutal-btn bg-primary text-primary-foreground px-4"><Send size={16} strokeWidth={2.5} /></button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
